import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import { WarningIcon } from "@chakra-ui/icons";
import { Box, HStack, Spinner, VStack } from "@chakra-ui/react";
import AppText from "../../components/Core/AppText/AppText";
import Breadcrumbs, { BreadcrumbItem } from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import CustomerHeader from "../../components/Shared/CustomerHeader";
import DetailPageTabs, {
	ListToolbarAction,
	RouteTabItem,
} from "../../components/Shared/DetailPageTabs";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import TagToggleSubMenu from "../../components/Shared/TagMenu/TagToggleSubMenu";
import { CustomerBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { routes } from "../../configs/constants/routes";
import { ViewModels } from "../../configs/constants/ViewModels";
import Customer from "../../models/Customer";
import { CONTRACT_SORTING_COLUMN } from "../../models/Sorting";
import { useContracts } from "../../networking/contract.hooks";
import {
	useCustomer,
	useCustomerDuplicates,
	useCustomerRating,
	useToggleCustomerTag,
	useUpdateCustomer,
} from "../../networking/customer.hooks";
import { useOrganisation } from "../../networking/organisation.hooks";
import { ConfirmModalProps } from "../../stores/confirmModal.store";
import { customerRoute, navigateToCreateCustomerUser } from "../../utils/route.utils";
import CustomerContractTab from "./CustomerContractTab";
import CustomerInvoiceTab from "./CustomerInvoiceTab";
import CustomerJobsTab from "./CustomerJobsTab";
import CustomerProfileTab from "./CustomerProfileTab";
import CustomerHistoryTab from "./CustomerHistoryTab";
import CustomerUsersTab from "./CustomerUsersTab";
import { useConfirmModalStore } from "../../stores/confirmModal.store";
import { useMediaStore } from "../../stores/media.store";

interface CustomerParamProps {
	customerId: string;
}

const CustomerDetailsPage: React.FC = () => {
	const mediaStore = useMediaStore();
	const modalStore = useConfirmModalStore();
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { customerId } = useParams<keyof CustomerParamProps>() as CustomerParamProps;

	const { contracts = [] } = useContracts({
		view: ViewModels.SELECT_OPTION,
		active: true,
		isDeleted: false,
		sort: CONTRACT_SORTING_COLUMN.NAME,
		pageSize: 1000,
	});

	const { data: organisation } = useOrganisation();

	const { customer, isLoading } = useCustomer(customerId, undefined, ["contract"]);
	const { rating } = useCustomerRating(customerId);
	const { mutate: updateCustomer } = useUpdateCustomer();
	const { mutate: toggleTag } = useToggleCustomerTag();

	// The ProfilePicture component (and the profile tab's save, which sends
	// `companyImage`) read the picture from mediaStore, so keep it in sync with
	// the loaded customer — this used to happen inside customerStore.loadCustomerById.
	useEffect(() => {
		mediaStore.setProfilePicture(customer?.companyImage || "");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [customer?.companyImage]);

	const changeActivation = (customer: Customer) => {
		updateCustomer({
			customerId,
			update: { active: customer?.active ? false : true },
		});
	};

	// Customer tags are descriptive labels without expiry — toggling adds or
	// removes the key (keys reference the org's customerTagTypes). Optimistic:
	// the mutation patches the ["customers", customerId] cache in onMutate, so
	// the header pills and the dropdown checkboxes flip instantly.
	const toggleCustomerTag = (customer: Customer, key: string) => {
		const current = customer.tags ?? [];
		const isAssigned = current.some((tag) => tag.key === key);
		const nextTags = isAssigned ? current.filter((tag) => tag.key !== key) : [...current, { key }];

		toggleTag({ customerId, nextTags });
	};

	const changeActivationConfirm = (customer: Customer) => {
		const type = customer?.active ? "DANGER" : "SUCCESS";
		const deleteConfirmModal: ConfirmModalProps = {
			title: `${customer?.active ? "DEACTIVATE_CUSTOMER_TITLE" : "ACTIVATE_CUSTOMER_TITLE"}`,
			text: `${customer?.active ? "DEACTIVATE_CUSTOMER_TEXT" : "ACTIVATE_CUSTOMER_TEXT"}`,
			acceptButtonText: "YES",
			rejectButtonText: "NO",
			onAccept: () => changeActivation(customer),
			onReject: () => {},
			type,
			body: customer.active ? null : <FindDuplicateCustomers customer={customer} />,
		};
		modalStore.newModal(deleteConfirmModal);
	};

	const handleUploadProfilePicture = useCallback(
		(pic: any) => {
			if (customerId) {
				updateCustomer({
					customerId,
					update: { companyImage: pic || "" },
				});
			}
		},
		[customerId, updateCustomer]
	);

	if (isLoading) {
		return <LoadingIndicator />;
	}

	if (!customer) {
		return null;
	}

	const sortedContracts = contracts.sort((a, b) => a.name.localeCompare(b.name, "sv"));

	const CUSTOMER_TAB_GROUPS: RouteTabItem[] = [
		{
			route: routes.CUSTOMER_PROFILE,
			label: "PROFILE",
			content: <CustomerProfileTab customer={customer} />,
		},
		{
			route: routes.CUSTOMER_JOBS,
			label: "JOBS",
			content: <CustomerJobsTab customer={customer} />,
		},
		{
			route: routes.CUSTOMER_INVOICE_DETAILS,
			label: "INVOICE_DETAILS",
			content: <CustomerInvoiceTab customer={customer} />,
		},
		{
			route: routes.CUSTOMER_CONTRACT,
			label: "CONTRACT_AND_PRICES",
			content: <CustomerContractTab customer={customer} contracts={sortedContracts} />,
		},
		{
			route: routes.CUSTOMER_USERS,
			label: "USERS",
			content: <CustomerUsersTab customer={customer} />,
		},
		{
			route: routes.CUSTOMER_HISTORY,
			label: "EVENTS",
			content: <CustomerHistoryTab customer={customer} />,
		},
	];

	const CustomerDetailsBreadcrumbs: BreadcrumbItem[] = [
		...CustomerBreadcrumbs,
		{
			title: `${customer?.name} (${customer?.customerNumber})`,
			path: customerRoute(customer.id),
		},
	];

	const customerActions: ListToolbarAction[] = [
		{
			label: "Lägg till användare",
			onClick: () => navigateToCreateCustomerUser(navigate, customer.id),
		},
		{
			label: customer?.active ? "DEACTIVATE" : "ACTIVATE",
			danger: customer?.active,
			dividerBefore: true,
			onClick: () => changeActivationConfirm(customer),
		},
	];

	return (
		<>
			<Breadcrumbs items={CustomerDetailsBreadcrumbs} />
			<div className="p-4 px-6">
				<CustomerHeader
					className="mb-3"
					customer={customer}
					rating={rating}
					tagTypes={organisation?.customerTagTypes}
					onToggleTag={(key) => toggleCustomerTag(customer, key)}
					onUploadProfilePicture={handleUploadProfilePicture}
				/>

				<DetailPageTabs
					tabs={CUSTOMER_TAB_GROUPS}
					actions={customerActions}
					actionsExtraItems={
						(organisation?.customerTagTypes?.length ?? 0) > 0 && (
							<TagToggleSubMenu
								tagTypes={organisation?.customerTagTypes ?? []}
								assignedTags={customer.tags ?? []}
								onToggle={(key) => toggleCustomerTag(customer, key)}
							/>
						)
					}
					contentClassName="pb-28"
				/>
			</div>
		</>
	);
};

export default CustomerDetailsPage;

interface FindDuplicateCustomersProps {
	customer: Customer;
}

const FindDuplicateCustomers: React.FC<FindDuplicateCustomersProps> = ({ customer }) => {
	const { data: dups, isLoading } = useCustomerDuplicates(customer.id);
	const { t } = useTranslation();

	return (
		<Box minH="6" w="full" mb="4">
			{isLoading && <Spinner />}
			{!!dups?.customers.length && (
				<>
					<HStack
						bg="red.50"
						borderColor="red.200"
						borderWidth={1}
						borderStyle={"solid"}
						p="2"
						mb="4"
					>
						<WarningIcon color="red.500" />
						<AppText>{t("customer.duplicates.warning")}</AppText>
					</HStack>
					<VStack maxH="300px" overflowX={"auto"} mb="2">
						{dups.customers.map((duplicateCustomer) => (
							<Box
								key={duplicateCustomer.id}
								bg="gray.100"
								w="100%"
								p="2"
								cursor="pointer"
								as="a"
								target="_blank"
								href={customerRoute(duplicateCustomer.id)}
							>
								<AppText weight="medium">
									{duplicateCustomer.name} (#{duplicateCustomer.customerNumber})
								</AppText>
							</Box>
						))}
					</VStack>
					<AppText size="xs" textAlign="center">
						{t("customer.duplicates.hint")}
					</AppText>
				</>
			)}
		</Box>
	);
};
